export type McpToolEffect =
  | 'read'
  | 'write'
  | 'credits'
  | 'outreach'
  | 'connect';

export type McpTool = {
  name: string;
  description: string;
  effect: McpToolEffect;
};

export type McpToolGroup = {
  id: string;
  title: string;
  description: string;
  tools: readonly McpTool[];
};

export const MCP_SKILL_NAMES = [
  'prospecting',
  'account_research',
  'account_monitors',
  'campaigns',
  'linkedin_network'
] as const;
export type McpSkillName = (typeof MCP_SKILL_NAMES)[number];
export const MCP_SKILL_INDEX = [
  {
    id: 'prospecting',
    title: 'Prospecting',
    description:
      'Choose a search approach, express the ICP clearly, review evidence, and diagnose weak results.'
  },
  {
    id: 'account_research',
    title: 'Account research',
    description:
      'Research a named company, review buying roles, validate account evidence, and prepare a useful brief.'
  },
  {
    id: 'account_monitors',
    title: 'Account monitors',
    description:
      'Launch and manage monitors, read saved assessments and CRM coverage, and compare periods honestly.'
  },
  {
    id: 'campaigns',
    title: 'Campaigns',
    description:
      'Decide how many campaigns to run, give different people different messages in one playbook, and set who is copied on emails.'
  },
  {
    id: 'linkedin_network',
    title: 'LinkedIn network search',
    description:
      'Find people the user, or someone they know, can reach through LinkedIn, and help ask for the intro.'
  }
] as const satisfies readonly {
  id: McpSkillName;
  title: string;
  description: string;
}[];

// Shared metadata for server navigation, marketing and documentation checks.
// Marketing targets the combined release; server navigation filters this catalog
// against actual successful, enabled registrations rather than promising future tools.
// Effects describe user-visible capabilities: report polling can reconcile stored
// status, and paid or outreach tools can reuse work without spending or sending.
export const MCP_TOOL_GROUPS: readonly McpToolGroup[] = [
  {
    id: 'projects',
    title: 'Workspace & prospect searches',
    description:
      'Find your workspace, search for buyers, and follow project progress.',
    tools: [
      {
        name: 'get-my-account',
        description:
          'Finds your profile, organizations, roles, and teammates so your assistant can choose the correct workspace.',
        effect: 'read'
      },
      {
        name: 'get-lumnis-skill',
        description:
          'Returns an authored prospecting, account research, account monitor or campaign setup guide as plain text without model calls, research, or credit spend.',
        effect: 'read'
      },
      {
        name: 'list-projects',
        description:
          'Lists your prospect search projects with filters and pagination, with options to include teammates and pipeline projects.',
        effect: 'read'
      },
      {
        name: 'get-project',
        description:
          'Shows search progress, judgment-filtered prospect previews, full prospect and campaign counts, and the sole live AI campaign when exactly one exists.',
        effect: 'read'
      },
      {
        name: 'create-project',
        description:
          'Spends credits to start a prospect search using profile criteria, buying signals, engagement, lookalikes, or a Sales Navigator source.',
        effect: 'credits'
      },
      {
        name: 'get-action-history',
        description:
          'Reads a project’s recent search actions and refreshes unfinished action statuses.',
        effect: 'read'
      }
    ]
  },
  {
    id: 'people',
    title: 'Prospect profiles & contacts',
    description:
      'Inspect individual buyers, previous interactions, and available contact details.',
    tools: [
      {
        name: 'list-prospects',
        description:
          'Filters project prospects by Verified, Needs review, or Flagged with full project counts, compact profiles, signals, and optional CRM context.',
        effect: 'read'
      },
      {
        name: 'find-linkedin-profiles',
        description:
          'Finds possible LinkedIn profiles from names and companies without saving prospects or charging customer credits.',
        effect: 'read'
      },
      {
        name: 'search-account-committee',
        description:
          'Finds an account’s committee using live search and reviewed criteria for a fixed credit charge. Reads saved results for free.',
        effect: 'credits'
      },
      {
        name: 'get-prospect',
        description:
          'Reads a prospect’s judgment, full profile, research, signals, and outreach history with optional CRM context.',
        effect: 'read'
      },
      {
        name: 'get-prospect-timeline',
        description:
          'Shows a prospect’s outbound actions, inbound replies, and pending next action in chronological order.',
        effect: 'read'
      },
      {
        name: 'enrich-contact-info',
        description:
          'Finds emails and optional phone numbers with provider verification status, spending credits for new lookups without saving results to the project.',
        effect: 'credits'
      }
    ]
  },
  {
    id: 'person-intelligence',
    title: 'Person Intelligence',
    description:
      'Research individual people from LinkedIn URLs or existing project prospects.',
    tools: [
      {
        name: 'launch-person-intelligence',
        description:
          'Starts paid public-source research for selected people or a project, always reusing active runs and reusing complete reports unless fresh research is requested.',
        effect: 'credits'
      },
      {
        name: 'get-person-intelligence',
        description:
          'Checks research by LinkedIn URL or project, showing running and partial-result flags with full reports available on request.',
        effect: 'read'
      }
    ]
  },
  {
    id: 'account-intelligence',
    title: 'Company Intelligence',
    description:
      'Research a target company and identify the people involved in buying.',
    tools: [
      {
        name: 'list-account-intelligence',
        description:
          'Lists researched company accounts with research status, run history, and deduplicated people counts.',
        effect: 'read'
      },
      {
        name: 'get-account-intelligence',
        description:
          'Reads a company’s facts, research report, buying committee, candidate people, campaigns, and run progress.',
        effect: 'read'
      },
      {
        name: 'draft-account-intelligence',
        description:
          'Researches a company and proposes buyer criteria for review without creating an account or starting the paid people search.',
        effect: 'read'
      },
      {
        name: 'launch-account-intelligence',
        description:
          'Uses reviewed buyer criteria to start paid company research and a people search, creating a project and creating or reusing its company account.',
        effect: 'credits'
      }
    ]
  },
  {
    id: 'account-monitors',
    title: 'Account Monitors',
    description:
      'Launch and manage monitors, then read their ranked results, saved assessments and CRM coverage.',
    tools: [
      {
        name: 'create-monitor',
        description: 'Creates a monitor from reviewed settings and accounts, or starts from an existing monitor’s settings; the first assessments start on the next worker tick or on a chosen first-run date.',
        effect: 'credits'
      },
      {
        name: 'run-monitor',
        description: 'Requests Run now using the monitor’s saved settings and dashboard credit policy.',
        effect: 'credits'
      },
      {
        name: 'add-monitor-accounts',
        description: 'Adds accounts and queues first assessments for active monitors using dashboard deduplication.',
        effect: 'credits'
      },
      {
        name: 'update-monitor',
        description: 'Updates settings, the weekly email and its list, or pauses/resumes a monitor; resuming can queue assessments and spend credits.',
        effect: 'credits'
      },
      {
        name: 'retry-monitor-assessment',
        description: 'Retries a failed assessment with its original inputs, or re-runs a scored one with the current monitor settings, under dashboard credit/refund rules.',
        effect: 'credits'
      },
      {
        name: 'set-monitor-account-owner',
        description: 'Changes one account’s owner label in a monitor, or clears it; spends no credits.',
        effect: 'write'
      },
      {
        name: 'send-monitor-test-email',
        description: 'Sends the latest weekly email of a monitor to you only, as a test; spends no credits.',
        effect: 'write'
      },
      {
        name: 'list-monitors',
        description:
          'Lists the organization’s account monitors with depth, cadence, account count, schedule, and the newest run’s status.',
        effect: 'read'
      },
      {
        name: 'get-monitor-rankings',
        description:
          'Reads one monitor run as the page shows it, with global ranks, priorities, scores, why-now reasons, owners, evidence flags, facets, and its evidence window.',
        effect: 'read'
      },
      {
        name: 'get-monitor-account-report',
        description:
          'Reads one account’s saved analysis from a run: verdict, why-now sections, next step, signal judgments, company and committee analysis, with cited evidence records on request.',
        effect: 'read'
      },
      {
        name: 'get-monitor-account-committee',
        description:
          'Reads the committee synthesis and bounded per-person reports for one saved assessment, active people first.',
        effect: 'read'
      },
      {
        name: 'get-monitor-account-timeline',
        description:
          'Lists one account’s saved assessments across runs and monitors with scores, statuses, evidence windows and the monitor each came from, for period-to-period comparison.',
        effect: 'read'
      },
      {
        name: 'list-crm-company-fields',
        description:
          'Lists the connected HubSpot or Attio company fields with their pick-list choices and flags the field that identifies the CRM owner.',
        effect: 'read'
      },
      {
        name: 'search-crm-companies',
        description:
          'Reads one page of CRM companies matching ticked pick-list values, with website domains and resolved owner names; the same filter can be saved as a monitor’s CRM source.',
        effect: 'read'
      }
    ]
  },
  {
    id: 'audience',
    title: 'Audience research',
    description:
      'Understand what an audience engages with and turn the findings into content ideas.',
    tools: [
      {
        name: 'list-audience-briefs',
        description:
          'Lists existing audience research briefs so you can find useful research before paying for another run.',
        effect: 'read'
      },
      {
        name: 'get-audience-brief',
        description:
          'Reads an audience brief’s engagement trends, pain points, audience language, top voices, curated posts, and ranked ideas with optional drafts.',
        effect: 'read'
      },
      {
        name: 'launch-audience-research',
        description:
          'Spends credits to analyze audience engagement and optionally competitor or expert publishing, producing research and content ideas without posting or contacting anyone.',
        effect: 'credits'
      }
    ]
  },
  {
    id: 'personas',
    title: 'Targeting personas',
    description:
      'Save and refine reusable criteria for the buyers you want to reach.',
    tools: [
      {
        name: 'list-personas',
        description:
          'Lists saved audience definitions, including roles, seniority, industries, locations, requirements, and exclusions.',
        effect: 'read'
      },
      {
        name: 'create-persona',
        description:
          'Saves reviewed targeting criteria as a reusable persona with an initial version, without launching research or a prospect search.',
        effect: 'write'
      },
      {
        name: 'update-persona',
        description:
          'Updates selected persona fields or list sections while preserving omitted fields and creating a reversible history version.',
        effect: 'write'
      }
    ]
  },
  {
    id: 'playbooks',
    title: 'Outreach playbooks',
    description:
      'Prepare the messaging strategy and instructions an AI campaign will follow.',
    tools: [
      {
        name: 'list-playbooks',
        description:
          'Lists available outbound playbooks, with an option to include archived playbooks.',
        effect: 'read'
      },
      {
        name: 'get-playbook',
        description:
          'Reads a playbook’s full text and parsed sections before you choose or edit its messaging strategy.',
        effect: 'read'
      },
      {
        name: 'create-playbook',
        description:
          'Creates an outbound playbook from supplied content or generates one from a goal for use in an AI campaign.',
        effect: 'write'
      },
      {
        name: 'update-playbook',
        description:
          'Changes a playbook’s name, complete text, or selected sections while preserving omitted sections and recording a new version.',
        effect: 'write'
      }
    ]
  },
  {
    id: 'campaigns',
    title: 'Campaigns & enrollment',
    description:
      'Add prospects, launch AI campaigns, adjust sending rules, and inspect performance.',
    tools: [
      {
        name: 'create-empty-project',
        description:
          'Creates a free project with no search, optionally adding people by LinkedIn URL, without enrolling anyone in outreach.',
        effect: 'write'
      },
      {
        name: 'add-prospects-to-project',
        description:
          'Adds manually supplied people to an existing project without search credits, identifying duplicates without enrolling them in outreach.',
        effect: 'write'
      },
      {
        name: 'add-prospects-to-crm',
        description:
          'Writes up to 50 people to the organization’s connected Attio or HubSpot, linking existing CRM records by LinkedIn, email or name and company and creating only when nothing matches; free.',
        effect: 'write'
      },
      {
        name: 'launch-ai-campaign',
        description:
          'Creates an AI campaign from LinkedIn URLs or project prospects and always starts real outreach, naming anyone the backend skips.',
        effect: 'outreach'
      },
      {
        name: 'add-prospects-to-campaign',
        description:
          'Enrolls project prospects or LinkedIn URLs into an existing AI campaign that may contact them, naming anyone the backend skips, with reconciliation available for uncertain previous outcomes.',
        effect: 'outreach'
      },
      {
        name: 'update-ai-campaign-settings',
        description:
          'Updates campaign sending rules, channels, and approval requirements, which can enable automatic outreach or social actions.',
        effect: 'outreach'
      },
      {
        name: 'get-campaign-stats',
        description:
          'Reads one campaign’s counts and status breakdowns, plus pending approvals and funnel stages for AI campaigns.',
        effect: 'read'
      },
      {
        name: 'pause-ai-campaign',
        description:
          'Pauses a running AI campaign without removing anyone or deleting anything.',
        effect: 'write'
      },
      {
        name: 'resume-ai-campaign',
        description:
          'Resumes a paused AI campaign, so its agent starts working its people again.',
        effect: 'outreach'
      },
      {
        name: 'remove-prospects-from-campaign',
        description:
          'Takes people out of an AI campaign while keeping them and their history in their project.',
        effect: 'write'
      },
      {
        name: 'delete-ai-campaign',
        description:
          'Stops an AI campaign and then hides it and frees its people, deleting nothing if the stop fails.',
        effect: 'write'
      }
    ]
  },
  {
    id: 'approvals',
    title: 'Human approvals',
    description:
      'Review pending actions with context before they reach real recipients.',
    tools: [
      {
        name: 'list-campaign-pending-actions',
        description:
          'Lists AI campaign actions awaiting review, including phone calls for the rep to make, with prospect details, category filters, and optional prior-interaction timelines.',
        effect: 'read'
      },
      {
        name: 'decide-campaign-actions',
        description:
          'Approves, rejects, or skips pending actions with optional message edits, executing approved actions from each campaign’s own sender account or recording a rep’s call with its outcome note.',
        effect: 'outreach'
      }
    ]
  },
  {
    id: 'pipelines',
    title: 'Pipeline reporting',
    description:
      'Inspect recurring outbound performance without changing or pausing a pipeline.',
    tools: [
      {
        name: 'list-pipelines',
        description:
          'Lists your or your teammates’ pipelines and summary metrics, with local contacted counts treated as a lower bound.',
        effect: 'read'
      },
      {
        name: 'get-pipeline-stats',
        description:
          'Reads one pipeline’s project and persona breakdowns alongside live outreach funnel counts for contacted people, replies, and booked meetings.',
        effect: 'read'
      }
    ]
  },
  {
    id: 'connections',
    title: 'LinkedIn & email connections',
    description:
      'Set up your own sending accounts through browser sign-in without sharing credentials in chat.',
    tools: [
      {
        name: 'get-linkedin-connection-status',
        description:
          'Checks your LinkedIn connection and may repair setup state, with only an active result confirming a completed connection.',
        effect: 'connect'
      },
      {
        name: 'connect-linkedin',
        description:
          'Returns a browser sign-in link for LinkedIn or reuses your active connection unless you explicitly request reconnection.',
        effect: 'connect'
      },
      {
        name: 'list-email-inboxes',
        description:
          'Lists sending inboxes for you or a selected org member, including stable mailbox IDs, without reading messages.',
        effect: 'read'
      },
      {
        name: 'setup-email-sender',
        description:
          'Creates or reuses your email sender identity from supplied personal details and a mailing address without provisioning domains or mailboxes.',
        effect: 'connect'
      },
      {
        name: 'connect-email-inbox',
        description:
          'Returns a browser sign-in link to connect your Gmail, Outlook, or IMAP sending inbox after sender setup.',
        effect: 'connect'
      }
    ]
  }
];

export const MCP_TOOL_COUNT = MCP_TOOL_GROUPS.reduce(
  (count, group) => count + group.tools.length,
  0
);
